import * as React from 'react'

import NextApp from 'next/app'
import getConfig from 'next/config'
import Head from 'next/head'

import { setServiceObject, withSentry } from '@grandvision/analytics'
import { AuthProvider, Routes, Types as AuthTypes } from '@grandvision/auth'
import {
  BloomreachContextSettings,
  BloomreachProvider,
  BloomreachService,
  BrPageModel,
  getClientSession,
  getServerSession,
  RuntimeContext,
  Session,
} from '@grandvision/bloomreach'
import {
  GoogleMapsProvider,
  GoogleMapsSettings,
  MarkerClusters,
  MediaQuery,
  Pin,
  ToastContainer,
} from '@grandvision/common'
import { RecaptchaProvider } from '@grandvision/forms'
import { DeliveryIndicatorProvider, StockIndicatorProvider } from '@grandvision/inventory'
import { withApollo, withRedux } from '@grandvision/next'
import {
  ApiConfigProvider,
  ApiConfiguration,
  Countries,
  Currencies,
  DistanceUnit,
  FeatureToggleProvider,
  IntlProvider,
  IntlSettings,
  Languages,
  mergeLabels,
  ReduxProvider,
  StoreWithPersistor,
} from '@grandvision/state'

import { isAABEnabled, isMultilingualEnabled, isMyAccountEnabled } from '../util/feature-toggle'
import ErrorPage from './_error'

import '../styles/index.scss'

export interface NextProcess extends NodeJS.Process {
  browser: boolean
}

const { publicRuntimeConfig } = getConfig()
const bloomreachSettings = publicRuntimeConfig?.bloomreach ?? {}
const defaultLocale = publicRuntimeConfig?.defaultLocale ?? 'es-ES'
const defaultCurrency = publicRuntimeConfig?.currency ?? Currencies.EUR
const {
  grandvision: {
    apiUrl,
    apiKey,
    endpoints,
    features,
    myAccount: {
      auth0Domain,
      auth0ClientId,
      auth0CustomDomain,
      putPrescriptionTokensOnCart = false as boolean,
      disableAddressEditing = false as boolean,
    },
  },
} = publicRuntimeConfig

const cmsService = BloomreachService(isMultilingualEnabled())

// My Account Routes
const routes: Routes = {
  createAccount: 'create',
  login: 'login',
  logout: 'logout',
  orders: 'orders',
  unlinkOrder: 'unlink-order',
  unlinkAppointment: 'unlink-appointment',
  signup: 'signup',
  privacy: 'privacy',
  profile: 'profile',
  addressBook: 'address-book',
  resetPassword: 'reset-password',
  linkExpired: 'link-expired',
  setPassword: 'set-password',
  changePassword: 'change-password',
}

const optIns: AuthTypes.OptInItem[] = [
  {
    titleKey: 'account.optIn.marketingEmails',
    value: 'subscribe-newsletter',
    tooltipKey: 'account.optIn.marketingEmails.tooltip',
    isInSignup: true,
  },
]

const authSettings = {
  apiUrl,
  basePath: 'my-account',
  clientId: auth0ClientId,
  customDomain: auth0CustomDomain,
  domain: auth0Domain,
  routes,
  checkoutRoute: '/checkout/shipping',
  putPrescriptionTokensOnCart,
  isAabEnabled: isAABEnabled(),
  disableAddressEditing,
  optIns,
}

const apiConfig: ApiConfiguration = {
  apiUrl,
  apiKey,
  cartsUrl: endpoints.carts,
  locationsUrl: endpoints.locations,
  ordersUrl: endpoints.orders,
  paymentsUrl: endpoints.payments,
  productsUrl: endpoints.products,
  storesUrl: endpoints.stores,
  subscriptionsUrl: endpoints.subscriptions,
}

const googleMapsSettings: GoogleMapsSettings = {
  apiKey: publicRuntimeConfig.googleMaps.apiKey,
  defaultCenter: publicRuntimeConfig.googleMaps.mapCenter,
  defaultZoom: 6,
  pin: Pin({
    themes: {
      primary: { foreground: '#ffffff', background: '#353935' },
      highlight: { foreground: '#ffffff', background: '#bb640e' },
    },
  }),
  markerClusterOptions: MarkerClusters({
    theme: {
      color: '#bb640e',
      textColor: '#ffffff',
    },
    minimumClusterSize: 3,
    maxZoom: 10,
  }),
}

/**
 * Remove locale options from the page model, because in BR we have Spanish and Catalan,
 * but only Spanish is enabled at the moment
 */
const removeLocaleOptionsFromPageModel = (pageModel: BrPageModel | undefined) => {
  if (pageModel?.page?.models?.localeOptions) {
    pageModel.page.models.localeOptions = []
  }

  return pageModel
}

type Props = {
  pageProps: any
  pageModel?: BrPageModel
  preview: boolean
  store: StoreWithPersistor
  labels: Record<string, Record<string, string>>
  errorFlag: boolean
  initialBreakpoint: MediaQuery.BreakpointName
  locale: string
  path?: string
  visitorId?: string
}

class App extends NextApp<Props> {
  static async getInitialProps({ Component, ctx }) {
    const nextProcess = process as NextProcess
    const session: Session =
      nextProcess && nextProcess.browser ? getClientSession({}) : getServerSession(ctx.req)

    cmsService.setCmsUrls(publicRuntimeConfig.bloomreach.cmsUrls)

    const preview = cmsService.isCmsPreview(session.search)
    const query: any = (ctx || {}).query || {}
    const locale: string = ctx.req?.locale?.toString() ?? defaultLocale
    const path = ctx.req?.originalUrl ?? '' // path + query params
    const request = {
      hostname: session.host,
      path,
    }

    let pageModel: BrPageModel | undefined = removeLocaleOptionsFromPageModel(query.pageModel)

    let errorFlag = false
    let data = ctx

    if (!pageModel) {
      try {
        pageModel = removeLocaleOptionsFromPageModel(
          await cmsService.fetchCmsPage(request, preview, locale, session.cookie)
        )
        const pageMeta = cmsService.getPageMeta(pageModel)

        data = {
          ...ctx,
          query: {
            search: query,
            request,
            preview,
            pageModel,
            locale,
            ...pageMeta,
          },
        }
      } catch (err) {
        console.error(err)
        errorFlag = true
      }
    }

    const tasks: Array<Promise<any>> = []

    // If we still don't have a pageModel (or have an error) by now, we should assume
    // bloomreach is not working as expected and don't try to fetch labels
    if (!errorFlag && pageModel) {
      tasks.push(
        cmsService.fetchCmsResource(
          bloomreachSettings.cmsUrls.live,
          bloomreachSettings.channel,
          ['*'], // the "*" fetches all available label resources
          locale
        )
      )
    }

    if (Component.getInitialProps && typeof Component.getInitialProps === 'function') {
      tasks.push(Component.getInitialProps.call(null, data))
    }

    const [labels, pageProps] = await Promise.all(tasks)

    return {
      locale,
      errorFlag,
      pageModel,
      preview,
      path,
      pageProps: pageProps || {},
      labels: labels || {},
      initialBreakpoint: ctx.req.mediaQueryBreakpoint,
      visitorId: query.bloomreachCmsSettings?.visitorId,
    }
  }

  componentDidMount() {
    // Analytics: set grandvision.service object
    setServiceObject()
  }

  render() {
    const {
      Component,
      pageProps,
      store,
      labels,
      errorFlag,
      locale,
      initialBreakpoint,
      pageModel,
      preview,
      path,
      visitorId,
    } = this.props

    if (errorFlag) {
      return <ErrorPage statusCode={500} locale={locale} />
    }

    const mergedLabels = mergeLabels(labels)
    const [language, country] = locale.split('-')
    const distanceUnit = DistanceUnit.km
    const intlSettings: IntlSettings = {
      language: Languages[language],
      country: Countries[country],
      currency: defaultCurrency,
      distanceUnit,
    }

    // omit `bloomreach` from `publicRuntimeConfig`
    const {
      bloomreach,
      deliveryIndicator,
      stockIndicator,
      ...cleanPublicRuntimeConfig
    } = publicRuntimeConfig

    const bloomreachContextSettings: BloomreachContextSettings = {
      page: { toJSON: () => pageModel, isPreview: () => preview },
      cmsUrls: bloomreachSettings.cmsUrls,
      visitorId,
      path,
    }

    return (
      <React.StrictMode>
        <Head>
          {/*https://github.com/vercel/next.js/blob/master/errors/no-document-viewport-meta.md*/}
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Head>
        <BloomreachProvider settings={bloomreachContextSettings}>
          <ReduxProvider store={store}>
            <FeatureToggleProvider enabledFeatures={features}>
              <IntlProvider settings={intlSettings} labels={mergedLabels}>
                <ApiConfigProvider apiConfig={apiConfig}>
                  <GoogleMapsProvider settings={googleMapsSettings}>
                    <StockIndicatorProvider settings={stockIndicator}>
                      <DeliveryIndicatorProvider settings={deliveryIndicator}>
                        <MediaQuery.Provider initialBreakpoint={initialBreakpoint}>
                          <AuthProvider enabled={isMyAccountEnabled()} settings={authSettings}>
                            <RecaptchaProvider settings={publicRuntimeConfig.recaptcha}>
                              <RuntimeContext.Provider
                                value={{ ...cleanPublicRuntimeConfig, headComponent: Head }}
                              >
                                <ToastContainer />
                                <Component {...pageProps} labels={labels} store={store} />
                              </RuntimeContext.Provider>
                            </RecaptchaProvider>
                          </AuthProvider>
                        </MediaQuery.Provider>
                      </DeliveryIndicatorProvider>
                    </StockIndicatorProvider>
                  </GoogleMapsProvider>
                </ApiConfigProvider>
              </IntlProvider>
            </FeatureToggleProvider>
          </ReduxProvider>
        </BloomreachProvider>
      </React.StrictMode>
    )
  }
}

export default withSentry(withRedux(withApollo()(App)))
